import { useState } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { OptionalLabel } from "@/components/OptionalLabel";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { misc } from "@/lib/misc";
import { cn } from "@/lib/utils";

export const FormCurrencyInput = <T extends Record<string, unknown>>({
  className,
  disabled,
  fieldName,
  form,
  inputClassName,
  label,
  onChange,
  optional,
  placeholder,
}: {
  className?: string;
  disabled?: boolean;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  inputClassName?: string;
  label: string;
  onChange?: (value: string) => void;
  optional?: boolean;
  placeholder?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        // TODO: Clean up logic here to make it more readable
        const displayValue =
          isEditing || (!field.value && field.value !== 0)
            ? // || "" is used to clear a field via form.setValue(fieldName, null). Without specifying "", it's interpreted as 0.
              ((field.value ?? "") as number)
            : // Enforce that field.value is treated as a number for the toLocaleString() call. Zod sends it in as a string.
              // Otherwise, commas aren't added to large numbers after editing a FormCurrencyInput.
              `$${Number(field.value).toLocaleString() || ""}`;

        return (
          <FormItem className={cn("text-slate-700", className)}>
            <FormLabel>
              {label}
              {optional && <OptionalLabel className="ml-1.5" />}
            </FormLabel>
            <FormControl>
              <Input
                className={inputClassName}
                disabled={disabled}
                value={displayValue}
                onBlur={() => {
                  setIsEditing(false);
                }}
                onChange={(e) => {
                  field.onChange(e);
                  onChange?.(e.target.value);
                }}
                onKeyDown={misc.enforceCurrencyInput}
                onFocus={() => {
                  setIsEditing(true);
                }}
                placeholder={placeholder}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
